import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../config/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';

// Async thunks for API calls
export const fetchIngredients = createAsyncThunk(
    'inventory/fetchIngredients',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(API_ENDPOINTS.INVENTORY_INGREDIENTS);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch ingredients');
        }
    }
);

export const createIngredient = createAsyncThunk(
    'inventory/createIngredient',
    async (ingredientData, { rejectWithValue }) => {
        try {
            const response = await axios.post(API_ENDPOINTS.INVENTORY_INGREDIENTS, ingredientData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create ingredient');
        }
    }
);

export const updateIngredient = createAsyncThunk(
    'inventory/updateIngredient',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await axios.put(API_ENDPOINTS.INVENTORY_INGREDIENT(id), data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update ingredient');
        }
    }
);

export const fetchRecipes = createAsyncThunk(
    'inventory/fetchRecipes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(API_ENDPOINTS.INVENTORY_RECIPES);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch recipes');
        }
    }
);

export const createRecipe = createAsyncThunk(
    'inventory/createRecipe',
    async (recipeData, { rejectWithValue }) => {
        try {
            const response = await axios.post(API_ENDPOINTS.INVENTORY_RECIPES, recipeData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create recipe');
        }
    }
);

export const fetchStockReport = createAsyncThunk(
    'inventory/fetchStockReport',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(API_ENDPOINTS.INVENTORY_STOCK_REPORT);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch stock report');
        }
    }
);

export const fetchCostAnalysis = createAsyncThunk(
    'inventory/fetchCostAnalysis',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(API_ENDPOINTS.INVENTORY_COST_ANALYSIS);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch cost analysis');
        }
    }
);

const inventorySlice = createSlice({
    name: 'inventory',
    initialState: {
        ingredients: [],
        recipes: [],
        stockReport: null,
        costAnalysis: [],
        loading: false,
        error: null
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Ingredients
            .addCase(fetchIngredients.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchIngredients.fulfilled, (state, action) => {
                state.loading = false;
                state.ingredients = action.payload;
            })
            .addCase(fetchIngredients.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createIngredient.fulfilled, (state, action) => {
                state.ingredients.push(action.payload);
            })
            .addCase(updateIngredient.fulfilled, (state, action) => {
                const index = state.ingredients.findIndex(ing => ing._id === action.payload._id);
                if (index !== -1) {
                    state.ingredients[index] = action.payload;
                }
            })
            // Recipes
            .addCase(fetchRecipes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchRecipes.fulfilled, (state, action) => {
                state.loading = false;
                state.recipes = action.payload;
            })
            .addCase(fetchRecipes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createRecipe.fulfilled, (state, action) => {
                state.recipes.push(action.payload);
            })
            // Reports
            .addCase(fetchStockReport.fulfilled, (state, action) => {
                state.stockReport = action.payload;
            })
            .addCase(fetchCostAnalysis.fulfilled, (state, action) => {
                state.costAnalysis = action.payload;
            });
    }
});

export const { clearError, setLoading } = inventorySlice.actions;
export default inventorySlice.reducer;